import ReactPlayer from "react-player";

/** Loom share / embed pathname → video id, or null. */
export function getLoomVideoId(href) {
  if (!href || typeof href !== "string") return null;
  let url;
  try {
    url = new URL(href.trim());
  } catch {
    return null;
  }
  if (!/^https?:$/i.test(url.protocol)) return null;

  const host = url.hostname.toLowerCase();
  if (host !== "www.loom.com" && host !== "loom.com") return null;

  const m = url.pathname.match(/^\/(?:share|embed)\/([^/?#]+)/);
  const id = m?.[1];
  if (!id || id.length < 8) return null;
  return id;
}

/**
 * Loom share / embed page → canonical embed iframe URL (react-player does not
 * ship a Loom player in v3).
 */
export function getLoomEmbedSrc(href) {
  const id = getLoomVideoId(href);
  return id ? `https://www.loom.com/embed/${id}` : null;
}

/** Normalize host / legacy video.php → watch URL for fb-video data-href. */
function normalizeFacebookPageUrl(url) {
  const u = new URL(url.toString());
  u.hash = "";
  u.protocol = "https:";
  const h = u.hostname.toLowerCase();
  if (
    h === "m.facebook.com" ||
    h === "web.facebook.com" ||
    h === "mbasic.facebook.com"
  ) {
    u.hostname = "www.facebook.com";
  } else if (h === "facebook.com") {
    u.hostname = "www.facebook.com";
  }
  if (
    u.pathname.includes("video.php") &&
    u.searchParams.has("v") &&
    !u.searchParams.has("story_fbid")
  ) {
    const vid = u.searchParams.get("v");
    return new URL(
      `https://www.facebook.com/watch?v=${encodeURIComponent(vid)}`,
    );
  }
  return u;
}

/**
 * Public Facebook video page URL for the official Embedded Video Player (fb-video).
 * Returns null if the link is not a supported video permalink.
 */
export function getFacebookVideoPageUrl(href) {
  if (!href || typeof href !== "string") return null;
  let url;
  try {
    url = new URL(href.trim());
  } catch {
    return null;
  }
  if (!/^https?:$/i.test(url.protocol)) return null;

  const host = url.hostname.toLowerCase();
  const isFbWatch = host === "fb.watch";
  const isFb =
    isFbWatch ||
    host === "facebook.com" ||
    host === "www.facebook.com" ||
    host === "m.facebook.com" ||
    host === "web.facebook.com" ||
    host === "mbasic.facebook.com";
  if (!isFb) return null;

  const path = url.pathname;
  const hasWatchV =
    (path === "/watch" || path.startsWith("/watch")) &&
    url.searchParams.has("v");
  const isReel = /\/reel\//i.test(path);
  const isPageVideo = /\/videos\/\d+/i.test(path);
  const isVideoPhp = /\/video\.php/i.test(path);
  const isStoryFbid = url.searchParams.has("story_fbid");
  const isShareVideo = /\/share\/(?:v|r)\//i.test(path);
  const isPostsVideo = /\/posts\/\d+/i.test(path);

  if (
    !isFbWatch &&
    !hasWatchV &&
    !isReel &&
    !isPageVideo &&
    !isVideoPhp &&
    !isStoryFbid &&
    !isShareVideo &&
    !isPostsVideo
  ) {
    return null;
  }

  try {
    return normalizeFacebookPageUrl(url).toString().split("#")[0];
  } catch {
    return null;
  }
}

/** Facebook video page URL → official plugins/video.php iframe src. */
export function getFacebookEmbedIframeSrc(pageUrl, { autoplay = false } = {}) {
  if (!pageUrl || typeof pageUrl !== "string") return null;
  try {
    const url = new URL("https://www.facebook.com/plugins/video.php");
    url.searchParams.set("href", pageUrl.trim());
    url.searchParams.set("show_text", "false");
    url.searchParams.set("allowfullscreen", "true");
    url.searchParams.set("width", "350");
    url.searchParams.set("height", "622");
    if (autoplay) {
      url.searchParams.set("autoplay", "true");
    }
    return url.toString();
  } catch {
    return null;
  }
}

/** Instagram reel / post / IGTV embed iframe. */
export function getInstagramEmbedSrc(href) {
  if (!href || typeof href !== "string") return null;
  let url;
  try {
    url = new URL(href.trim());
  } catch {
    return null;
  }
  if (!/^https?:$/i.test(url.protocol)) return null;

  const host = url.hostname.toLowerCase();
  if (host !== "instagram.com" && host !== "www.instagram.com") return null;

  const m = url.pathname.match(/^\/(reel|p|tv)\/([^/?#]+)/);
  if (!m) return null;
  return `https://www.instagram.com/${m[1]}/${m[2]}/embed`;
}

/** Request autoplay when the embed is mounted after a direct user click on the preview. */
export function withEmbedAutoplay(embedSrc) {
  if (!embedSrc || typeof embedSrc !== "string") return embedSrc;
  try {
    const url = new URL(embedSrc);
    const host = url.hostname.toLowerCase();
    if (host.includes("loom.com") || host.includes("instagram.com")) {
      url.searchParams.set("autoplay", "1");
    }
    if (host.includes("fathom.video")) {
      url.searchParams.set("autoplay", "true");
    }
    return url.toString();
  } catch {
    return embedSrc;
  }
}

/** Read.ai / Fathom meeting links — text bookmark cards, not inline video players. */
export function isHostedBookmarkCardLink(href) {
  if (!href || typeof href !== "string") return false;
  const t = href.trim();
  if (!t) return false;
  if (getReadAiMeetingId(t)) return true;
  return /fathom\.video\/calls\/[a-zA-Z0-9_-]+/i.test(t);
}

export function isEmbeddableHostedVideoLink(href) {
  if (!href || typeof href !== "string") return false;
  const t = href.trim();
  if (!t) return false;
  if (isHostedBookmarkCardLink(t)) return false;
  if (getPlayableVideoUrl(t) || getLoomEmbedSrc(t)) return true;
  if (getFacebookVideoPageUrl(t) || getInstagramEmbedSrc(t)) return true;
  if (/spotify\.com/i.test(t)) return false;
  return typeof ReactPlayer.canPlay === "function" && ReactPlayer.canPlay(t);
}

/** Plain http(s) links that should render as large chat cards (not inline text). */
export function isExtractableChatLink(href) {
  if (!href || typeof href !== "string") return false;
  const t = href.trim();
  if (!t || isEmbeddableHostedVideoLink(t) || isHostedBookmarkCardLink(t)) {
    return false;
  }
  try {
    const url = new URL(t);
    return /^https?:$/i.test(url.protocol);
  } catch {
    return false;
  }
}

/**
 * Returns a URL string suitable for react-player, or null if the href is not
 * a known public YouTube / Vimeo watch link.
 */
export function getPlayableVideoUrl(href) {
  if (!href || typeof href !== "string") return null;
  const trimmed = href.trim();
  let url;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (!/^https?:$/i.test(url.protocol)) return null;

  const host = url.hostname.replace(/^www\./, "").toLowerCase();

  if (host === "youtu.be") {
    const id = url.pathname.replace(/^\//, "").split("/")[0];
    if (id) return `https://www.youtube.com/watch?v=${id}`;
    return null;
  }

  if (
    host === "youtube.com" ||
    host === "m.youtube.com" ||
    host === "music.youtube.com"
  ) {
    if (url.pathname === "/watch" || url.pathname.startsWith("/watch")) {
      const v = url.searchParams.get("v");
      if (v) return `https://www.youtube.com/watch?v=${v}`;
    }
    const shorts = url.pathname.match(/^\/shorts\/([\w-]+)/);
    if (shorts?.[1]) return `https://www.youtube.com/watch?v=${shorts[1]}`;
    const embed = url.pathname.match(/^\/embed\/([\w-]+)/);
    if (embed?.[1]) return `https://www.youtube.com/watch?v=${embed[1]}`;
    const live = url.pathname.match(/^\/live\/([\w-]+)/);
    if (live?.[1]) return `https://www.youtube.com/watch?v=${live[1]}`;
    return null;
  }

  if (host === "vimeo.com") {
    const parts = url.pathname.split("/").filter(Boolean);
    const id = parts[0];
    if (id && /^\d+$/.test(id)) return `https://vimeo.com/${id}`;
    return null;
  }

  if (host === "player.vimeo.com") {
    const m = url.pathname.match(/\/video\/(\d+)/);
    if (m?.[1]) return `https://vimeo.com/${m[1]}`;
  }

  return null;
}

/** YouTube watch URL → 11-char video id, or null. */
export function getYoutubeVideoId(playableUrl) {
  if (!playableUrl || typeof playableUrl !== "string") return null;
  const m = playableUrl.match(/[?&]v=([\w-]{11})/);
  return m ? m[1] : null;
}

/**
 * Static poster for react-player `light` (YouTube). Avoids oEmbed/noembed
 * when that request is blocked by network or CSP.
 *
 * Uses maxresdefault (up to ~1280×720). hqdefault/mqdefault are only 480×360
 * and 320×180 — they look soft when the embed is full-width.
 * Some very old clips lack maxres; YouTube may serve a smaller asset for the same URL.
 */
export function getVideoLightPosterUrl(playableUrl) {
  const id = getYoutubeVideoId(playableUrl);
  if (id) {
    // hqdefault is available for virtually all public videos (maxres often 404s)
    return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  return null;
}

function getVimeoPosterUrl(href) {
  const playable = getPlayableVideoUrl(href);
  if (!playable) return null;
  const id = playable.match(/vimeo\.com\/(\d+)/)?.[1];
  return id ? `https://vumbnail.com/${id}.jpg` : null;
}

function getLoomPosterUrl(href) {
  const id = getLoomVideoId(href);
  if (!id) return null;
  return `https://cdn.loom.com/sessions/thumbnails/${id}-00001.jpg`;
}

/** Ordered static Loom thumbnail guesses before oEmbed resolves. */
export function getLoomPosterFallbacks(href) {
  const id = getLoomVideoId(href);
  if (!id) return [];
  return [
    `https://cdn.loom.com/sessions/thumbnails/${id}-00001.jpg`,
    `https://cdn.loom.com/sessions/thumbnails/${id}-00001.gif`,
    `https://cdn.loom.com/sessions/thumbnails/${id}.jpg`,
  ];
}

/**
 * Fetch the best poster via oEmbed (Loom / Facebook). Falls back to static URL.
 */
export async function fetchHostedVideoPosterUrl(href) {
  const t = (href || "").trim();
  if (!t) return null;

  const sync = getHostedVideoPosterUrl(t);
  const loomId = getLoomVideoId(t);
  const fbPage = getFacebookVideoPageUrl(t);

  if (loomId) {
    try {
      const shareUrl = `https://www.loom.com/share/${loomId}`;
      const res = await fetch(
        `https://www.loom.com/v1/oembed?url=${encodeURIComponent(shareUrl)}`,
      );
      if (res.ok) {
        const data = await res.json();
        if (data?.thumbnail_url) return data.thumbnail_url;
      }
    } catch {
      // use static fallbacks below
    }
  }

  if (fbPage) {
    const endpoints = [
      `https://api.microlink.io/?url=${encodeURIComponent(fbPage)}`,
      `https://api.microlink.io/?url=${encodeURIComponent(fbPage)}&screenshot=true&meta=false`,
    ];
    for (const endpoint of endpoints) {
      try {
        const res = await fetch(endpoint);
        if (!res.ok) continue;
        const data = await res.json();
        const imageUrl = data?.data?.image?.url || data?.data?.screenshot?.url;
        if (imageUrl) return imageUrl;
      } catch {
        // try next source
      }
    }
  }

  return sync;
}

/** Best-effort static poster for the click-to-play overlay (YouTube, Vimeo, Loom). */
export function getHostedVideoPosterUrl(href) {
  const t = (href || "").trim();
  if (!t) return null;
  const playable = getPlayableVideoUrl(t);
  return (
    getVideoLightPosterUrl(playable || t) ||
    getVimeoPosterUrl(t) ||
    getLoomPosterUrl(t) ||
    null
  );
}

// Fathom: https://fathom.video/calls/CALL_ID  →  embed
export function getFathomEmbedSrc(url) {
  const match = url.match(/fathom\.video\/calls\/([a-zA-Z0-9_-]+)/i);
  return match ? `https://fathom.video/calls/${match[1]}/embed` : null;
}

// Read.ai: https://read.ai/review/MEETING_ID  →  embed
export function getReadAiEmbedSrc(url) {
  const match = url.match(
    /app\.read\.ai\/analytics\/meetings\/([a-zA-Z0-9_-]+)/i,
  );
  return match ? `https://app.read.ai/analytics/meetings/${match[1]}` : null;
}

// Read.ai meeting id from common share URLs
export function getReadAiMeetingId(url) {
  const t = (url || "").trim();
  const patterns = [
    /app\.read\.ai\/analytics\/meetings\/([a-zA-Z0-9_-]+)/i,
    /read\.ai\/review\/([a-zA-Z0-9_-]+)/i,
    /read\.ai\/share\/([a-zA-Z0-9_-]+)/i,
    /app\.read\.ai\/share\/([a-zA-Z0-9_-]+)/i,
  ];
  for (const re of patterns) {
    const match = t.match(re);
    if (match?.[1]) return match[1];
  }
  return null;
}
